import React from 'react';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import urlParse from 'url-parse';

import SubscribeToNewsletter from '@components/Newsletter/SubscribeToNewsletter';
import RelatedStoriesMidArticle from '@components/RelatedStories/MidArticle';
import EndOfArticleDeepResearch from '@components/pages/Article/EndOfArticle/DeepResearch';
import LegacyDeepResearch from '@components/pages/Article/MidArticle/DeepResearch';
import MidArticleEvent from '@components/pages/Article/MidArticle/Event';
import { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import { SuggestedAiPrompt } from '@components/pages/Article/interfaces';
import { Article } from '@interfaces/homepage';
import { READER_URL_PRODUCTION } from '@src/env';

const MID_ARTICLE_DEEP_RESEARCH_UNIT = 'mid_article_deep_research_unit';
const MID_ARTICLE_EVENT_UNIT = 'mid_article_event_unit';
const MID_ARTICLE_RECIRC_UNIT = 'mid_article_recirc_unit';
const NEWSLETTER_SUBSCRIBE_HTML = 'newsletter_subscribe_unit';

const transformLinksToBeExternal = (node) => {
  if (node.type === 'tag' && node.name === 'a') {
    const href = node.attribs?.href;

    // Ignore jump and mail to links
    if (href && !(href.startsWith('#') || href.startsWith('mailto:'))) {
      const url = urlParse(href, true);

      if (url.host !== urlParse(READER_URL_PRODUCTION).host) {
        // rc query params are often included in links by mistake by authors
        // copying URLs.
        if (url.query && url.query.rc) {
          delete url.query.rc;
        }

        return {
          ...node,
          attribs: {
            ...node.attribs,
            href: url.toString(),
            target: '_blank', // Set links to open in a new tab
          },
        };
      }
    }
  }

  return node;
};

const transformers = (node) => {
  return [transformLinksToBeExternal].reduce((accNode, tf) => {
    return tf(accNode);
  }, node);
};

const transform = (node, index) => convertNodeToElement(transformers(node), index, transform);

const articleTransform = (
  node,
  index,
  newsletterId,
  relatedArticles,
  relatedEvent,
  articleId,
  deepResearchPrompts,
  suggestedAiPrompts
) => {
  if (node?.attribs?.class?.includes('embedded-timeline') && node?.attribs?.style) {
    node.attribs.style = '';
  } else if (node.name === MID_ARTICLE_DEEP_RESEARCH_UNIT) {
    if (suggestedAiPrompts?.length) {
      return <LegacyDeepResearch key={`deep-research-module-${index}`} />;
    }
    if (deepResearchPrompts?.length) {
      return (
        <EndOfArticleDeepResearch
          key={`deep-research-module-${index}`}
          id={articleId}
          prompts={deepResearchPrompts}
        />
      );
    }
    return null;
  } else if (node.name === MID_ARTICLE_EVENT_UNIT && relatedEvent) {
    return <MidArticleEvent key={`mid-article-event-${index}`} event={relatedEvent} />;
  } else if (node.name === NEWSLETTER_SUBSCRIBE_HTML) {
    return <SubscribeToNewsletter key={`newsletter-subscribe-${index}`} id={newsletterId} />;
  } else if (node.name === MID_ARTICLE_RECIRC_UNIT && relatedArticles) {
    return (
      <RelatedStoriesMidArticle
        key={`related-stories-mid-article-${index}`}
        articles={relatedArticles}
      />
    );
  }
  return convertNodeToElement(transformers(node), index, articleTransform);
};

const AuthoredContentReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform });
};

const AuthoredArticleContentReactToHtmlParser = ({
  text,
  newsletterId,
  relatedArticles = [],
  relatedEvent,
  articleId,
  deepResearchPrompts,
  suggestedAiPrompts,
}: {
  text: string;
  newsletterId?: string | number;
  relatedArticles?: Article[];
  relatedEvent?: MidArticleEventProps;
  articleId?: number;
  deepResearchPrompts?: string[];
  suggestedAiPrompts?: SuggestedAiPrompt[];
}) => {
  return ReactHtmlParser(text, {
    transform: (node, index) =>
      articleTransform(
        node,
        index,
        newsletterId,
        relatedArticles,
        relatedEvent,
        articleId,
        deepResearchPrompts,
        suggestedAiPrompts
      ),
  });
};

export { AuthoredArticleContentReactToHtmlParser, AuthoredContentReactToHtmlParser };
